'use client'

import Image from 'next/image'
import { cn } from '@/lib/utils'
import { useDocumentTheme } from '@/lib/use-document-theme'

/**
 * Shared logo-strip presentation. Three layouts, one renderer:
 * - wrap: centered flex-wrap row (MonitoringStackLogos)
 * - marquee: doubled scrolling row with edge fades (CompanyLogos) — reuses
 *   the existing .animate-scroll keyframe (reduced-motion guarded in CSS);
 *   no new animation introduced.
 * - grid: fused bordered cells, one logo per cell (homepage TrustSection).
 *
 * Theme handling: items either provide light/dark asset pairs, or a single
 * src with an optional per-theme CSS filter (grayscale treatments).
 * BrandLogoStrip (scored data strip) is intentionally NOT on this primitive.
 */
export interface LogoStripItem {
  name: string
  /** Single asset (used with `filter` theming). */
  src?: string
  /** Theme-specific asset pair (wins over src when present). */
  light?: string
  dark?: string
  /** Intrinsic dimensions for next/image; height falls back to displayH. */
  width?: number
  height?: number
  /** Rendered height in px (optical balance per logo). */
  displayH?: number
}

interface LogoStripProps {
  items: LogoStripItem[]
  layout?: 'wrap' | 'marquee' | 'grid'
  /**
   * CSS filter per theme, for assets that ship one src and need inverting in
   * dark (e.g. MonitoringStackLogos). Being a function, it can only be passed
   * from a Client Component — a Server Component parent throws at render.
   * A theme-independent treatment belongs in `itemClassName` instead
   * (`grayscale opacity-60`), which works from either side of the boundary.
   */
  filterFor?: (theme: 'light' | 'dark') => string | undefined
  /** Overrides every item's displayH — one dial for a whole strip. */
  logoHeight?: number
  className?: string
  itemClassName?: string
}

export function LogoStrip({
  items,
  layout = 'wrap',
  filterFor,
  logoHeight,
  className,
  itemClassName,
}: LogoStripProps) {
  const theme = useDocumentTheme()
  const filter = filterFor?.(theme)

  const renderLogo = (item: LogoStripItem, key: string) => {
    const src = (theme === 'dark' ? item.dark : item.light) ?? item.src
    if (!src) return null
    const displayH = logoHeight ?? item.displayH ?? 40
    return (
      <Image
        key={key}
        src={src}
        alt={item.name}
        width={item.width ?? 0}
        height={item.height ?? displayH}
        unoptimized={src.includes('.png') || Boolean(item.src)}
        className={cn('w-auto object-contain', itemClassName)}
        style={{ height: `${displayH}px`, ...(filter ? { filter } : {}) }}
      />
    )
  }

  if (layout === 'grid') {
    return (
      <div
        className={cn(
          'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 border-t border-l border-line',
          className
        )}
      >
        {items.map(item => (
          <div
            key={item.name}
            className='grid place-items-center min-h-[84px] p-4 border-r border-b border-line'
          >
            {renderLogo(item, item.name)}
          </div>
        ))}
      </div>
    )
  }

  if (layout === 'wrap') {
    return (
      <div
        className={cn(
          'flex flex-wrap items-center justify-center gap-x-8 gap-y-4',
          className
        )}
      >
        {items.map(item => renderLogo(item, item.name))}
      </div>
    )
  }

  return (
    <div className={cn('relative w-full overflow-hidden', className)}>
      {/* Continuous marquee — no edge fades/masks; the doubled item set plus
          the -50% translate keyframe makes the loop seamless. */}
      <div className='flex animate-scroll'>
        {[0, 1].map(dup =>
          items.map(item => (
            <div
              key={`${dup}-${item.name}`}
              className='flex-shrink-0 mx-8 w-40 h-20 flex items-center justify-center overflow-hidden'
            >
              {renderLogo(item, `${dup}-${item.name}-img`)}
            </div>
          ))
        )}
      </div>
    </div>
  )
}

export default LogoStrip
